import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Row, Col, Button } from 'reactstrap';
import { ArrowLeft, BarChart3 } from 'lucide-react';
import LoadingScreen from 'src/Loading/LoadingScreen';
import {
  getComplianceDashboard,
  getComplianceForEachStandardDashboard,
} from 'src/services/dashboards';
import PieChart from 'src/components/charts/charts/PieChart';
import BarChart from 'src/components/charts/charts/BarChart';
import LineChart from 'src/components/charts/charts/LineChart';
import './home.css';

/**
 * Parent-tenant view of one child organization (e.g. CHUM under Madinet Masr):
 * compliance status, standards breakdown, and monthly task trend — aligned with
 * supervisor/management tiers in the CHUM field operations design.
 */
const ChildTenantFieldOperations = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const [searchParams] = useSearchParams();
  const displayName = searchParams.get('name') || '';
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [complianceStatus, setComplianceStatus] = useState<any[]>([]);
  const [monthlyTasks, setMonthlyTasks] = useState<any[]>([]);
  const [complianceForEachStandard, setComplianceForEachStandard] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getComplianceDashboard(tenantId);
      if (data.success) {
        setDashboardData(data?.data);
        setComplianceStatus([
          {
            Compliance: data?.data?.compliance,
            Non_Compliance: data?.data?.non_compliance,
            Partially: data?.data?.partially,
            Not_Started: data?.data?.not_started,
            In_Progress: data?.data?.in_progress,
            Done: data?.data?.done,
          },
        ]);
        const monthlyTasksWithMonthNames = (data.data.monthlyTasks || []).map((task: any, index: number) => {
          const monthNames = [
            t('Dashboard.Jan'),
            t('Dashboard.Feb'),
            t('Dashboard.Mar'),
            t('Dashboard.Apr'),
            t('Dashboard.May'),
            t('Dashboard.Jun'),
            t('Dashboard.Jul'),
            t('Dashboard.Aug'),
            t('Dashboard.Sep'),
            t('Dashboard.Oct'),
            t('Dashboard.Nov'),
            t('Dashboard.Dec'),
          ];
          return { name: monthNames[index] ?? String(index + 1), noTasks: task };
        });
        setMonthlyTasks(monthlyTasksWithMonthNames);
      } else {
        setDashboardData(null);
      }

      const std = await getComplianceForEachStandardDashboard(tenantId);
      if (std.success && std.data) {
        const formattedData = Object.keys(std.data).map((key: string) => ({
          id: key,
          reference_name: std.data[key].reference_name,
          fully_implemented: std.data[key].fully_implemented,
          mostly_implemented: std.data[key].mostly_implemented,
          averagely_implemented: std.data[key].averagely_implemented,
          implemented: std.data[key].implemented,
          partially_compliant: std.data[key].partially_compliant,
          non_compliant: std.data[key].non_compliant,
        }));
        setComplianceForEachStandard(formattedData);
      } else {
        setComplianceForEachStandard([]);
      }
    } catch (e: any) {
      setError(e?.message || t('institution.field_ops_load_error'));
      setDashboardData(null);
    } finally {
      setLoading(false);
    }
  }, [tenantId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!tenantId) {
    return null;
  }

  if (loading) {
    return (
      <div className="loading-screen" style={{ backgroundColor: 'rgba(255, 255, 255, 0.85)' }}>
        <LoadingScreen />
      </div>
    );
  }

  return (
    <div className="inst-sup-page inst-field-ops-page px-3 py-3">
      <div className="d-flex align-items-center gap-2 mb-3">
        <Button color="link" className="p-0 text-decoration-none d-inline-flex align-items-center gap-1" onClick={() => navigate('/institutionSup')}>
          <ArrowLeft size={18} />
          <span>{t('institution.field_ops_back')}</span>
        </Button>
      </div>

      <div className="inst-field-ops-hero mb-4">
        <div className="d-flex align-items-start gap-3">
          <div className="inst-field-ops-icon" aria-hidden>
            <BarChart3 size={22} />
          </div>
          <div>
            <h1 className="h4 mb-1 fw-bold">{t('institution.field_ops_title')}</h1>
            <p className="text-muted small mb-0">
              {displayName ? (
                <>
                  <span className="fw-semibold text-dark">{displayName}</span>
                  {' · '}
                </>
              ) : null}
              {t('institution.field_ops_subtitle')}
            </p>
          </div>
        </div>
      </div>

      {error ? (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      ) : null}

      <Row className="g-3 mb-3">
        <Col sm={12} md={4}>
          <div className="inst-field-ops-kpi">
            <span className="inst-field-ops-kpi-label">{t('institution.field_ops_kpi_compliant')}</span>
            <span className="inst-field-ops-kpi-value text-success">{dashboardData?.compliance ?? 0}</span>
          </div>
        </Col>
        <Col sm={12} md={4}>
          <div className="inst-field-ops-kpi">
            <span className="inst-field-ops-kpi-label">{t('institution.field_ops_kpi_non_compliant')}</span>
            <span className="inst-field-ops-kpi-value text-danger">{dashboardData?.non_compliance ?? 0}</span>
          </div>
        </Col>
        <Col sm={12} md={4}>
          <div className="inst-field-ops-kpi">
            <span className="inst-field-ops-kpi-label">{t('institution.field_ops_kpi_in_progress')}</span>
            <span className="inst-field-ops-kpi-value text-warning">{dashboardData?.in_progress ?? 0}</span>
          </div>
        </Col>
      </Row>

      <Row className="g-3 mb-4">
        <Col xs={12} lg={4}>
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h5 className="card-title fw-semibold">{t('Dashboard.Reference/Compliance')}</h5>
              <hr className="my-3" />
              <div className="chart-container" style={{ height: 300 }}>
                <BarChart data={complianceForEachStandard} />
              </div>
            </div>
          </div>
        </Col>
        <Col xs={12} lg={4}>
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h5 className="card-title fw-semibold">{t('Dashboard.Simple_Tasks')}</h5>
              <hr className="my-3" />
              <div className="chart-container" style={{ height: 300 }}>
                <LineChart data={monthlyTasks} />
              </div>
            </div>
          </div>
        </Col>
        <Col xs={12} lg={4}>
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h5 className="card-title fw-semibold">{t('Dashboard.Compliance_Status')}</h5>
              <hr className="my-3" />
              <div className="chart-container" style={{ height: 300 }}>
                <PieChart data={complianceStatus} />
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default ChildTenantFieldOperations;
